/*
 * itrh-base.js — shared UI components for all new sections.
 *
 *  window.itrh.load(name)  Lazy-loads a vendor library once ('embla' | 'floating').
 *  [data-reveal]           Fade + slide-in when scrolled into view (Theme settings > Animations).
 *  [data-reveal-zoom]      Image zoom-out when scrolled into view.
 *  <itrh-carousel>         Embla carousel: drag, loop, autoplay, arrows, dots.
 *  <itrh-dropdown>         Floating UI dropdown: auto-positioning, animated, lazy-rendered.
 *  <itrh-drawer>           Slide-in panel: overlay, focus trap, scroll lock, nested pages.
 *  <itrh-predictive-search> Live search results from Shopify's predictive search API.
 */
(() => {
  if (window.itrh) return;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const modules = {};

  const globals = { embla: 'ItrhVendorEmbla', floating: 'ItrhVendorFloating' };

  const itrh = (window.itrh = {
    // Loads a vendor library with a classic <script> (works on Shopify's CDN without module/CORS issues).
    load(name) {
      const url = window.itrhAssets?.[name];
      const globalName = globals[name];
      if (!url || !globalName) return Promise.reject(new Error(`itrh: unknown library "${name}"`));
      if (window[globalName]) return Promise.resolve(window[globalName]);
      return (modules[name] ||= new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => (window[globalName] ? resolve(window[globalName]) : reject(new Error(`itrh: ${name} failed`)));
        script.onerror = () => {
          delete modules[name];
          reject(new Error(`itrh: ${name} failed to load`));
        };
        document.head.append(script);
      }));
    },
    reducedMotion,
  });

  /* ---------------------------------------------------------------- Reveal */

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  itrh.reveal = (root = document) => {
    const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed), [data-reveal-zoom]:not(.is-revealed)');
    targets.forEach((el) => (revealObserver ? revealObserver.observe(el) : el.classList.add('is-revealed')));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => itrh.reveal(), { once: true });
  } else {
    itrh.reveal();
  }
  document.addEventListener('shopify:section:load', (e) => itrh.reveal(e.target));

  /* -------------------------------------------------------------- Carousel */

  class ItrhCarousel extends HTMLElement {
    async connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      if (!this.viewport || this.embla) return;

      const slides = this.viewport.firstElementChild?.children.length || 0;
      if (slides < 2) {
        this.setAttribute('data-static', '');
        return;
      }

      let lib;
      try {
        lib = await itrh.load('embla');
      } catch (_) {
        this.initNative();
        return;
      }
      const { EmblaCarousel, Autoplay, AutoScroll } = lib;
      if (!this.isConnected || this.embla) return;

      const data = this.dataset;
      const delay = Number(data.autoplay || 0) * 1000;
      const speed = Number(data.autoscroll || 0);
      const plugins = [];

      if (speed > 0 && AutoScroll && !reducedMotion.matches) {
        // Continuous, gliding motion that never stops between slides
        this.autoplay = AutoScroll({
          speed,
          startDelay: 0,
          stopOnInteraction: false,
          stopOnMouseEnter: data.hoverPause !== 'false',
          stopOnFocusIn: true,
        });
        plugins.push(this.autoplay);
      } else if (delay && !reducedMotion.matches) {
        this.autoplay = Autoplay({
          delay,
          stopOnInteraction: false,
          stopOnMouseEnter: data.hoverPause !== 'false',
          stopOnFocusIn: true,
        });
        plugins.push(this.autoplay);
      }

      this.embla = EmblaCarousel(
        this.viewport,
        {
          loop: data.loop !== undefined,
          align: data.align || 'start',
          axis: data.axis || 'x',
          containScroll: data.loop !== undefined ? false : 'trimSnaps',
          watchDrag: data.drag !== 'false',
          duration: reducedMotion.matches ? 10 : Number(data.duration || 30),
          direction: getComputedStyle(this).direction === 'rtl' ? 'rtl' : 'ltr',
        },
        plugins
      );

      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.dots = this.querySelector('[data-carousel-dots]');

      this.onPrev = () => {
        this.embla.scrollPrev();
        this.autoplay?.reset();
      };
      this.onNext = () => {
        this.embla.scrollNext();
        this.autoplay?.reset();
      };
      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);

      this.buildDots();
      this.update = this.update.bind(this);
      this.embla.on('select', this.update).on('slidesInView', this.update).on('reInit', () => {
        this.buildDots();
        this.update();
      });
      this.embla.on('pointerDown', () => this.classList.add('is-dragging'));
      this.embla.on('pointerUp', () => this.classList.remove('is-dragging'));

      this.setAttribute('data-ready', '');
      this.update();
    }

    disconnectedCallback() {
      this.prevBtn?.removeEventListener('click', this.onPrev);
      this.nextBtn?.removeEventListener('click', this.onNext);
      this.embla?.destroy();
      this.embla = null;
      this.autoplay = null;
    }

    buildDots() {
      if (!this.dots) return;
      const count = this.embla.scrollSnapList().length;
      this.dots.replaceChildren(
        ...Array.from({ length: count }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'itrh-carousel__dot';
          dot.setAttribute('aria-label', `${i + 1} / ${count}`);
          dot.addEventListener('click', () => {
            this.embla.scrollTo(i);
            this.autoplay?.reset();
          });
          return dot;
        })
      );
    }

    update() {
      const selected = this.embla.selectedScrollSnap();
      const inView = new Set(this.embla.slidesInView());

      this.embla.slideNodes().forEach((slide, i) => {
        slide.classList.toggle('is-selected', i === selected);
        slide.toggleAttribute('inert', !inView.has(i) && i !== selected);
      });

      if (this.prevBtn) this.prevBtn.disabled = !this.embla.canScrollPrev();
      if (this.nextBtn) this.nextBtn.disabled = !this.embla.canScrollNext();

      this.dots?.querySelectorAll('.itrh-carousel__dot').forEach((dot, i) => {
        dot.setAttribute('aria-current', i === selected ? 'true' : 'false');
      });
    }

    goTo(index, instant = false) {
      this.embla?.scrollTo(index, instant);
    }

    /* Fallback when the carousel library cannot load: native swipe + working arrows */
    initNative() {
      if (this.native) return;
      this.native = true;
      this.setAttribute('data-native', '');
      const viewport = this.viewport;
      const prev = this.querySelector('[data-carousel-prev]');
      const next = this.querySelector('[data-carousel-next]');
      const step = (dir) => {
        const slide = viewport.querySelector('.itrh-carousel__slide');
        const width = slide ? slide.getBoundingClientRect().width : viewport.clientWidth;
        viewport.scrollBy({ left: dir * width, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      const sync = () => {
        if (prev) prev.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
      };
      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      viewport.addEventListener('scroll', sync, { passive: true });
      sync();
    }
  }

  if (!customElements.get('itrh-carousel')) customElements.define('itrh-carousel', ItrhCarousel);

  // Theme editor: jump to the selected block's slide and pause autoplay.
  document.addEventListener('shopify:block:select', (e) => {
    const carousel = e.target.closest?.('itrh-carousel');
    if (!carousel?.embla) return;
    carousel.autoplay?.stop();
    carousel.goTo(carousel.embla.slideNodes().indexOf(e.target), true);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    e.target.closest?.('itrh-carousel')?.autoplay?.play();
  });

  /* -------------------------------------------------------------- Dropdown */

  let dropdownId = 0;

  class ItrhDropdown extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-dropdown-toggle]');
      this.template = this.querySelector('template');
      if (!this.toggle || !this.template) return;

      this.onToggle = () => (this.isOpen ? this.close() : this.open());
      this.onOutside = (e) => {
        if (!this.panel.contains(e.target) && !this.toggle.contains(e.target)) this.close();
      };
      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close(true);
      };
      this.onFocusOut = (e) => {
        if (e.relatedTarget && !this.panel.contains(e.relatedTarget) && !this.toggle.contains(e.relatedTarget)) {
          this.close();
        }
      };

      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.addEventListener('click', this.onToggle);
      // Warm up the library on first hover/focus so opening feels instant.
      this.toggle.addEventListener('pointerenter', () => itrh.load('floating'), { once: true });
      this.toggle.addEventListener('focus', () => itrh.load('floating'), { once: true });
    }

    disconnectedCallback() {
      this.close();
      this.toggle?.removeEventListener('click', this.onToggle);
      this.panel?.remove();
      this.panel = null;
    }

    render() {
      this.panel = this.template.content.firstElementChild.cloneNode(true);
      this.panel.id ||= `ItrhDropdown-${++dropdownId}`;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-controls', this.panel.id);

      this.panel.addEventListener('input', (e) => {
        if (!e.target.matches('[data-dropdown-search]')) return;
        const query = e.target.value.trim().toLowerCase();
        this.panel.querySelectorAll('[data-search-value]').forEach((item) => {
          item.hidden = query !== '' && !item.dataset.searchValue.includes(query);
        });
      });

      // Items with data-dropdown-value: announce the choice on the dropdown element and close.
      this.panel.addEventListener('click', (e) => {
        const item = e.target.closest('[data-dropdown-value]');
        if (!item) return;
        e.preventDefault();
        this.panel.querySelectorAll('[data-dropdown-value]').forEach((el) => el.setAttribute('aria-current', String(el === item)));
        const label = this.toggle.querySelector('[data-dropdown-label]');
        if (label) label.textContent = item.textContent.trim();
        this.dispatchEvent(new CustomEvent('itrh:dropdown-select', { bubbles: true, detail: { value: item.dataset.dropdownValue } }));
        this.close(true);
      });

      document.body.append(this.panel);
    }

    async open() {
      if (!this.panel) this.render();
      this.isOpen = true;
      clearTimeout(this.hideTimer);

      const { computePosition, autoUpdate, offset, flip, shift } = await itrh.load('floating');
      if (!this.isOpen) return;

      const placement = this.dataset.placement || 'bottom-end';
      const mainAxis = Number(this.dataset.offset ?? 12);
      const alignmentAxis = Number(this.dataset.alignOffset ?? 0);

      this.panel.hidden = false;
      this.cleanup?.();
      this.cleanup = autoUpdate(this.toggle, this.panel, () => {
        computePosition(this.toggle, this.panel, {
          placement,
          strategy: 'fixed',
          middleware: [offset({ mainAxis, alignmentAxis }), flip({ padding: 12 }), shift({ padding: 12 })],
        }).then(({ x, y, placement: final }) => {
          this.panel.style.left = `${x}px`;
          this.panel.style.top = `${y}px`;
          this.panel.dataset.placement = final;
        });
      });

      requestAnimationFrame(() => this.panel.classList.add('is-open'));
      this.toggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      document.addEventListener('pointerdown', this.onOutside);
      document.addEventListener('keydown', this.onKeydown);
      this.panel.addEventListener('focusout', this.onFocusOut);

      const focusTarget =
        this.panel.querySelector('[data-dropdown-search]') ||
        this.panel.querySelector('[aria-current="true"]') ||
        this.panel.querySelector('button, a, input');
      focusTarget?.focus({ preventScroll: true });
    }

    close(returnFocus = false) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.toggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('keydown', this.onKeydown);

      if (this.panel) {
        this.panel.removeEventListener('focusout', this.onFocusOut);
        this.panel.classList.remove('is-open');
        this.hideTimer = setTimeout(() => {
          if (this.isOpen || !this.panel) return;
          this.panel.hidden = true;
          this.cleanup?.();
          this.cleanup = null;
        }, reducedMotion.matches ? 0 : 250);
      }

      if (returnFocus) this.toggle.focus();
    }
  }

  if (!customElements.get('itrh-dropdown')) customElements.define('itrh-dropdown', ItrhDropdown);
  /* ---------------------------------------------------------------- Drawer */

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let openDrawers = 0;

  const lockScroll = (lock) => {
    const html = document.documentElement;
    if (lock) {
      if (openDrawers++ === 0) {
        document.body.style.setProperty('--itrh-scrollbar', `${Math.max(innerWidth - html.clientWidth, 0)}px`);
        document.body.classList.add('itrh-scroll-lock');
      }
    } else if (openDrawers > 0 && --openDrawers === 0) {
      document.body.classList.remove('itrh-scroll-lock');
    }
  };

  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  class ItrhDrawer extends HTMLElement {
    connectedCallback() {
      // Portal to <body> so no transformed/sticky ancestor can trap the fixed panel.
      if (this.parentElement !== document.body) {
        this.dataset.owner = this.closest('.shopify-section')?.id || '';
        document.body.append(this);
        return;
      }
      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('.itrh-drawer__panel');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;

      // Aurora close cursor: a round × that follows the mouse over the overlay.
      const overlay = this.querySelector('.itrh-drawer__overlay');
      if (overlay) {
        this.cursor = document.createElement('span');
        this.cursor.className = 'itrh-close-cursor';
        this.cursor.setAttribute('aria-hidden', 'true');
        this.cursor.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';
        this.append(this.cursor);
        this.setAttribute('data-close-cursor', '');

        const moveCursor = (e) => {
          if (!finePointer.matches || !this.isOpen) return;
          this.cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
          this.cursor.classList.add('is-visible');
        };
        overlay.addEventListener('pointerenter', moveCursor);
        overlay.addEventListener('pointermove', moveCursor);
        overlay.addEventListener('pointerleave', () => this.cursor.classList.remove('is-visible', 'is-pressed'));
        overlay.addEventListener('pointerdown', () => this.cursor.classList.add('is-pressed'));
      }

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) return this.close();
        const pageOpen = e.target.closest('[data-page-open]');
        if (pageOpen) return this.goToPage(pageOpen.dataset.pageOpen);
        if (e.target.closest('[data-page-back]')) this.pageBack();
      });

      this.onKeydown = (e) => {
        if (e.key === 'Escape' && !document.querySelector('.itrh-dropdown.is-open')) {
          e.preventDefault();
          this.close();
        } else if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      };
    }

    disconnectedCallback() {
      if (this.isOpen && !this.isConnected) {
        lockScroll(false);
        document.removeEventListener('keydown', this.onKeydown);
      }
    }

    open(trigger) {
      if (this.isOpen || !this.ready) return;
      this.isOpen = true;
      this.trigger = trigger;
      clearTimeout(this.resetTimer);

      if (this.hasAttribute('data-below-header')) {
        const header = document.querySelector('itrh-header');
        const bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
        this.style.setProperty('--drawer-top', `${bottom}px`);
      }

      this.inert = false;
      this.removeAttribute('aria-hidden');
      lockScroll(true);
      requestAnimationFrame(() => this.classList.add('is-open'));
      trigger?.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKeydown);
      this.dispatchEvent(new CustomEvent('itrh:drawer-open', { bubbles: true, detail: { trigger } }));

      setTimeout(() => {
        const target = this.querySelector('[autofocus], [data-search-input]') || this.panel?.querySelector(FOCUSABLE);
        target?.focus({ preventScroll: true });
      }, reducedMotion.matches ? 0 : 120);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.cursor?.classList.remove('is-visible', 'is-pressed');
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      lockScroll(false);
      document.removeEventListener('keydown', this.onKeydown);
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('itrh:drawer-close', { bubbles: true, detail: { trigger: this.trigger } }));
      this.resetTimer = setTimeout(() => this.resetPages(), 500);
    }

    toggle(trigger) {
      this.isOpen ? this.close() : this.open(trigger);
    }

    trapFocus(e) {
      const items = [...(this.panel || this).querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    goToPage(id) {
      const next = this.querySelector(`[data-page="${CSS.escape(id)}"]`);
      const current = this.querySelector('[data-page].is-active');
      if (!next || next === current) return;
      (this.history ||= []).push(current);
      current.classList.remove('is-active');
      current.classList.add('is-behind');
      next.classList.add('is-active');
      next.scrollTop = 0;
      next.querySelector('[data-page-back]')?.focus({ preventScroll: true });
    }

    pageBack() {
      const previous = this.history?.pop();
      const current = this.querySelector('[data-page].is-active');
      if (!previous || !current) return;
      current.classList.remove('is-active');
      previous.classList.remove('is-behind');
      previous.classList.add('is-active');
    }

    resetPages() {
      const pages = this.querySelectorAll('[data-page]');
      if (!pages.length) return;
      pages.forEach((page) => page.classList.remove('is-active', 'is-behind'));
      this.querySelector('[data-page="root"]')?.classList.add('is-active');
      this.history = [];
    }
  }

  if (!customElements.get('itrh-drawer')) customElements.define('itrh-drawer', ItrhDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.toggle !== 'function') return;
    e.preventDefault();
    drawer.toggle(trigger);
  });

  // Theme editor: remove portaled drawers when their section re-renders.
  document.addEventListener('shopify:section:unload', (e) => {
    document.querySelectorAll(`itrh-drawer[data-owner="${e.target.id}"]`).forEach((drawer) => drawer.remove());
  });

  /* ------------------------------------------------------ Predictive search */

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch (_) {
      return value.toFixed(2);
    }
  };

  const sizedImage = (src, width) => {
    try {
      const url = new URL(src, location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (_) {
      return src;
    }
  };

  class ItrhPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-results]');
      if (!this.input || !this.results) return;

      this.onInput = () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.search(), 220);
      };
      this.input.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.input?.removeEventListener('input', this.onInput);
    }

    async search() {
      const query = this.input.value.trim();
      this.controller?.abort();
      this.classList.toggle('has-query', query !== '');

      if (!query) {
        this.results.innerHTML = '';
        this.classList.remove('is-loading', 'has-results');
        return;
      }

      this.controller = new AbortController();
      this.classList.add('is-loading');

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
        // Match SKUs, tags, colours and categories, not just titles
        'resources[options][fields]': 'title,product_type,variants.sku,variants.title,vendor,tag',
      });

      try {
        const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.controller.signal });
        const data = await response.json();
        this.render(data.resources?.results || {}, query);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render({ products = [], collections = [], queries = [] }, query) {
      const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      this.classList.add('has-results');

      if (!products.length && !collections.length && !queries.length) {
        this.results.innerHTML = `<p class="itrh-search__empty">No results for “${escapeHtml(query)}”</p>`;
        return;
      }

      let html = '';

      if (queries.length || collections.length) {
        html += '<div class="itrh-search__group"><p class="itrh-search__label">Suggestions</p><ul class="itrh-search__chips" role="list">';
        html += queries
          .slice(0, 4)
          .map((q, i) => `<li style="--i:${i}"><a class="itrh-search__chip" href="${escapeHtml(q.url)}">${escapeHtml(q.text)}</a></li>`)
          .join('');
        html += collections
          .slice(0, 4)
          .map((c, i) => `<li style="--i:${i + 4}"><a class="itrh-search__chip itrh-search__chip--collection" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a></li>`)
          .join('');
        html += '</ul></div>';
      }

      if (products.length) {
        html += '<div class="itrh-search__group"><p class="itrh-search__label">Products</p><ul class="itrh-search__products" role="list">';
        html += products
          .map((p, i) => {
            const price = Number(p.price);
            const compare = Number(p.compare_at_price_max);
            const image = p.featured_image?.url || p.image;
            return `<li style="--i:${i}">
              <a class="itrh-search__product" href="${escapeHtml(p.url)}">
                <span class="itrh-search__media">${image ? `<img src="${escapeHtml(sizedImage(image, 360))}" alt="" loading="lazy" width="360" height="480">` : ''}</span>
                <span class="itrh-search__title">${escapeHtml(p.title)}</span>
                <span class="itrh-search__price">
                  ${compare > price ? `<s>${formatMoney(compare)}</s>` : ''}
                  <span>${formatMoney(price)}</span>
                </span>
              </a>
            </li>`;
          })
          .join('');
        html += '</ul></div>';
      }

      html += `<a class="itrh-search__all" href="${escapeHtml(searchUrl)}"><span class="itrh-underline">View all results</span></a>`;
      this.results.innerHTML = html;
    }
  }

  if (!customElements.get('itrh-predictive-search')) customElements.define('itrh-predictive-search', ItrhPredictiveSearch);
  /* ------------------------------------------------ Product card slideshow */

  const hoverCapable = matchMedia('(hover: hover) and (pointer: fine)');

  const setCardSlide = (media, index) => {
    const slides = media.querySelectorAll('.itrh-card__slide');
    const bars = media.querySelectorAll('.itrh-card__bar');
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    bars.forEach((bar, i) => bar.classList.toggle('is-active', i === index));
  };

  // Desktop: moving the mouse across the image switches photos (Aurora behaviour).
  document.addEventListener('pointerover', (e) => {
    if (!hoverCapable.matches) return;
    const zone = e.target.closest('[data-card-zone]');
    if (!zone) return;
    const media = zone.closest('[data-card-slides]');
    if (media) setCardSlide(media, Number(zone.dataset.cardZone));
  });

  document.addEventListener('pointerout', (e) => {
    if (!hoverCapable.matches) return;
    const media = e.target.closest?.('[data-card-slides]');
    if (media && !media.contains(e.relatedTarget)) setCardSlide(media, 0);
  });

  // Touch: the track is a swipeable strip; keep the bars in sync.
  document.addEventListener('scroll', (e) => {
    const track = e.target;
    if (!(track instanceof Element) || !track.matches('.itrh-card__track')) return;
    const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
    track.closest('[data-card-slides]')?.querySelectorAll('.itrh-card__bar').forEach((bar, i) => {
      bar.classList.toggle('is-active', i === index);
    });
  }, true);

  // Touch: a swipe must not also follow the product link.
  let cardSwipeX = null;
  document.addEventListener('touchstart', (e) => {
    cardSwipeX = e.target.closest('[data-card-slides]') ? e.touches[0].clientX : null;
  }, { passive: true });
  document.addEventListener('click', (e) => {
    if (cardSwipeX === null) return;
    const media = e.target.closest('[data-card-slides]');
    if (media && media.dataset.swiped === 'true') {
      e.preventDefault();
      media.dataset.swiped = 'false';
    }
  }, true);
  document.addEventListener('touchmove', (e) => {
    if (cardSwipeX === null) return;
    const media = e.target.closest('[data-card-slides]');
    if (media && Math.abs(e.touches[0].clientX - cardSwipeX) > 10) media.dataset.swiped = 'true';
  }, { passive: true });

  /* --------------------------------------------------------- Quick view */

  const QuickView = {
    drawer: null,
    url: '',

    ensure() {
      if (this.drawer?.isConnected) return this.drawer;
      const drawer = document.createElement('itrh-drawer');
      drawer.id = 'ItrhQuickView';
      drawer.className = 'itrh-qv-drawer';
      drawer.dataset.side = 'right';
      drawer.innerHTML = `
        <div class="itrh-drawer__overlay" data-drawer-close></div>
        <div class="itrh-drawer__panel itrh-qv-drawer__panel" role="dialog" aria-modal="true" aria-label="Quick view">
          <div class="itrh-drawer__header">
            <span class="itrh-drawer__title">Quick view</span>
            <button type="button" class="itrh-drawer__close" data-drawer-close aria-label="Close">
              <svg class="itrh-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
            </button>
          </div>
          <div class="itrh-qv-drawer__body" data-qv-body></div>
        </div>`;
      document.body.append(drawer);
      this.drawer = drawer;
      return drawer;
    },

    body() {
      return this.drawer.querySelector('[data-qv-body]');
    },

    loading() {
      this.body().innerHTML = '<div class="itrh-qv__loading"><span></span></div>';
    },

    endpoint(url, extra = '') {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', 'itrh-quick-view');
      if (extra) new URLSearchParams(extra).forEach((v, k) => u.searchParams.set(k, v));
      return u.pathname + u.search;
    },

    async fetchRoot(url) {
      const html = await (await fetch(url)).text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-qv-root]');
    },

    async open(url, trigger) {
      this.url = url;
      const drawer = this.ensure();
      await customElements.whenDefined('itrh-drawer');
      this.loading();
      drawer.open(trigger);
      try {
        const root = await this.fetchRoot(this.endpoint(url));
        if (!root) throw new Error('Quick view unavailable');
        this.body().replaceChildren(root);
        this.syncDots(root);
      } catch (_) {
        window.location.href = url;
      }
    },

    async changeVariant(root, focusId) {
      const ids = [...root.querySelectorAll('[data-qv-picker] input:checked')].map((el) => el.dataset.optionValueId).join(',');
      root.classList.add('is-updating');
      try {
        const next = await this.fetchRoot(this.endpoint(root.dataset.url, `option_values=${ids}`));
        if (!next) return;
        const scroll = this.body().scrollTop;
        root.replaceWith(next);
        this.body().scrollTop = scroll;
        const variant = JSON.parse(next.querySelector('[data-qv-variant]')?.textContent || 'null');
        if (!variant) {
          const add = next.querySelector('[data-qv-add]');
          if (add) {
            add.disabled = true;
            add.querySelector('span').textContent = 'Unavailable';
          }
        }
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        this.syncDots(next);
      } finally {
        root.classList.remove('is-updating');
      }
    },

    async addToCart(form) {
      const button = form.querySelector('[data-qv-add]');
      const error = form.querySelector('[data-qv-error]');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      error.hidden = true;

      const cart = document.querySelector('cart-drawer');
      const data = new FormData(form);
      if (cart?.getSectionsToRender) {
        data.append('sections', cart.getSectionsToRender().map((section) => section.id));
        data.append('sections_url', location.pathname);
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        });
        const result = await response.json();
        if (result.status) {
          error.textContent = result.description || result.message || 'Something went wrong.';
          error.hidden = false;
          return;
        }
        if (!cart?.renderContents) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: result });
        }
        this.drawer.close();
        setTimeout(() => cart.renderContents(result), 250);
      } catch (_) {
        error.textContent = 'Something went wrong. Please try again.';
        error.hidden = false;
      } finally {
        button.classList.remove('is-loading');
      }
    },

    syncDots(root) {
      const track = root.querySelector('[data-qv-track]');
      const dots = root.querySelectorAll('.itrh-qv__dot');
      if (!track) return;
      const update = () => {
        const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        const prev = root.querySelector('[data-qv-step="-1"]');
        const next = root.querySelector('[data-qv-step="1"]');
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= track.children.length - 1;
      };
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      update();
    },
  };

  window.itrh.quickView = QuickView;

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      QuickView.open(trigger.dataset.quickView, trigger);
      return;
    }

    const root = e.target.closest('[data-qv-root]');
    if (!root) return;

    const step = e.target.closest('[data-qv-step]');
    if (step) {
      const track = root.querySelector('[data-qv-track]');
      track.scrollBy({ left: Number(step.dataset.qvStep) * track.clientWidth, behavior: 'smooth' });
      return;
    }

    const qty = e.target.closest('[data-qv-qty]');
    if (qty) {
      const input = qty.parentElement.querySelector('input');
      const inc = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      let value = Math.max(min, (Number(input.value) || min) + Number(qty.dataset.qvQty) * inc);
      if (input.max) value = Math.min(Number(input.max), value);
      input.value = value;
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qv-picker] input');
    if (!input) return;
    const root = input.closest('[data-qv-root]');
    if (input.dataset.productUrl && input.dataset.productUrl !== root.dataset.url) {
      QuickView.open(input.dataset.productUrl);
      return;
    }
    const label = input.closest('.itrh-qv__option')?.querySelector('.itrh-qv__selected');
    if (label) label.textContent = input.value;
    QuickView.changeVariant(root, input.id);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-qv-root] .itrh-qv__form');
    if (!form) return;
    e.preventDefault();
    QuickView.addToCart(form);
  });

  /* --------------------------------------------------------- Hotspot pins */

  const hotspotsMobile = matchMedia('(max-width: 767px)');


  

  class ItrhHotspots extends HTMLElement {
    connectedCallback() {
      this.pins = [...this.querySelectorAll('[data-pin]')];
      this.place();

      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-pin-toggle]');
        if (toggle) {
          const pin = toggle.closest('[data-pin]');
          this.open(pin, !pin.classList.contains('is-open'));
          return;
        }
        if (e.target.closest('[data-pin-close]')) this.closeAll();
      });

      this.addEventListener('pointerenter', (e) => {
        if (this.dataset.trigger !== 'hover' || hotspotsMobile.matches) return;
        const pin = e.target.closest?.('[data-pin]');
        if (pin) this.open(pin, true);
      }, true);

      this.addEventListener('pointerleave', (e) => {
        if (this.dataset.trigger !== 'hover' || hotspotsMobile.matches) return;
        const pin = e.target.closest?.('[data-pin]');
        if (pin && !pin.contains(e.relatedTarget)) this.open(pin, false);
      }, true);

      this.addEventListener('focusin', (e) => {
        const pin = e.target.closest('[data-pin]');
        if (pin) this.open(pin, true);
      });

      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.closeAll();
      };
      this.onKey = (e) => {
        if (e.key === 'Escape') this.closeAll();
      };
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);

      this.onResize = () => this.place();
      addEventListener('resize', this.onResize, { passive: true });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      removeEventListener('resize', this.onResize);
    }

    // Decide which way each card opens so it stays inside the image
    place() {
      const small = hotspotsMobile.matches;
      this.pins.forEach((pin) => {
        const x = Number(small ? pin.dataset.xMobile : pin.dataset.x);
        const y = Number(small ? pin.dataset.yMobile : pin.dataset.y);
        pin.classList.toggle('is-below', y < 45);
        pin.classList.toggle('is-left', x < 22);
        pin.classList.toggle('is-right', x > 78);
      });
    }

    open(pin, show) {
      if (show) this.pins.forEach((other) => other !== pin && other.classList.remove('is-open'));
      pin.classList.toggle('is-open', show);
      pin.querySelector('[data-pin-toggle]')?.setAttribute('aria-expanded', String(show));
    }

    closeAll() {
      this.pins.forEach((pin) => {
        pin.classList.remove('is-open');
        pin.querySelector('[data-pin-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }
  }

  if (!customElements.get('itrh-hotspots')) customElements.define('itrh-hotspots', ItrhHotspots);

  /* ------------------------------- Collection and search results (filters, sort) */


  const STORAGE_KEY = 'itrh-collection-cols';

  class ItrhCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.cache = new Map();

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPop = () => this.render(window.location.search.slice(1), false);
      this.onSort = (e) => {
        if (!e.target.matches('[data-sort-dropdown]')) return;
        const input = this.querySelector('[data-sort-select]');
        if (!input || input.value === e.detail.value) return;
        input.value = e.detail.value;
        this.render(this.buildQuery());
      };
      this.addEventListener('itrh:dropdown-select', this.onSort);

      document.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onClick);
      document.addEventListener('input', this.onChange);
      window.addEventListener('popstate', this.onPop);

      this.applyStoredColumns();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('input', this.onChange);
      window.removeEventListener('popstate', this.onPop);
    }

    get drawer() {
      return document.getElementById(this.dataset.drawer);
    }

    owns(el) {
      return this.contains(el) || this.drawer?.contains(el);
    }

    /* Build the query from the filter form + sort */
    buildQuery() {
      const params = new URLSearchParams();
      const form = this.drawer?.querySelector('[data-filter-form]');
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
      }
      const sort = this.querySelector('[data-sort-select]');
      if (sort) params.set('sort_by', sort.value);
      return params.toString();
    }

    onChange(e) {
      const target = e.target;
      if (!this.owns(target)) return;
      const isPrice = target.matches('.itrh-price-range input');
      if (e.type === 'input' && !isPrice) return;
      if (e.type === 'change' && isPrice) return;
      if (!target.matches('[data-filter-form] input')) return;

      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(this.buildQuery()), isPrice ? 700 : 150);
    }

    onClick(e) {
      const target = e.target;
      if (!this.owns(target)) return;

      const link = target.closest('[data-filter-link]');
      if (link) {
        e.preventDefault();
        const isPage = link.closest('.itrh-pagination');
        this.render(new URL(link.href, window.location.origin).searchParams.toString(), true, isPage);
        return;
      }

      const facet = target.closest('[data-facet-toggle]');
      if (facet) {
        const item = facet.closest('.itrh-facet');
        const open = !item.classList.contains('is-open');
        item.classList.toggle('is-open', open);
        facet.setAttribute('aria-expanded', String(open));
        return;
      }

      const colD = target.closest('[data-cols-d]');
      const colM = target.closest('[data-cols-m]');
      if (colD || colM) {
        const stored = this.readColumns();
        if (colD) stored.d = colD.dataset.colsD;
        if (colM) stored.m = colM.dataset.colsM;
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch (_) {}
        this.applyStoredColumns();
      }
    }

    readColumns() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (_) {
        return {};
      }
    }

    applyStoredColumns() {
      const stored = this.readColumns();
      const d = stored.d || getComputedStyle(this).getPropertyValue('--coll-cols').trim();
      const m = stored.m || getComputedStyle(this).getPropertyValue('--coll-cols-m').trim();
      if (stored.d) this.style.setProperty('--coll-cols', stored.d);
      if (stored.m) this.style.setProperty('--coll-cols-m', stored.m);
      this.querySelectorAll('[data-cols-d]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.colsD === String(d))));
      this.querySelectorAll('[data-cols-m]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.colsM === String(m))));
    }

    async render(query, push = true, scrollTop = false) {
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      this.classList.add('is-loading');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const fetchUrl = `${window.location.pathname}?${query ? `${query}&` : ''}section_id=${this.sectionId}`;
          html = await (await fetch(fetchUrl)).text();
          this.cache.set(url, html);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('itrh-collection');
        if (!source) return;

        // Toolbar, chips and results inside the section
        ['vendors', 'toolbar', 'active', 'results'].forEach((key) => {
          const next = source.querySelector(`[data-coll-update="${key}"]`);
          const current = this.querySelector(`[data-coll-update="${key}"]`);
          if (next && current) current.replaceWith(next);
        });

        // Filter form inside the portaled drawer: keep open facets + focus
        const drawer = this.drawer;
        const nextForm = source.querySelector('[data-coll-update="filters"]');
        const currentForm = drawer?.querySelector('[data-coll-update="filters"]');
        if (nextForm && currentForm) {
          const open = new Set([...currentForm.querySelectorAll('.itrh-facet.is-open')].map((f) => f.dataset.facet));
          const focusedName = document.activeElement?.name;
          const focusedValue = document.activeElement?.value;
          const scroll = currentForm.querySelector('.itrh-filters__body')?.scrollTop || 0;

          nextForm.querySelectorAll('.itrh-facet').forEach((f) => {
            const isOpen = open.has(f.dataset.facet);
            f.classList.toggle('is-open', isOpen);
            f.querySelector('[data-facet-toggle]')?.setAttribute('aria-expanded', String(isOpen));
          });
          currentForm.replaceWith(nextForm);
          nextForm.querySelector('.itrh-filters__body').scrollTop = scroll;
          if (focusedName) {
            const el = [...nextForm.querySelectorAll(`[name="${CSS.escape(focusedName)}"]`)].find(
              (i) => i.type !== 'checkbox' || i.value === focusedValue
            );
            el?.focus({ preventScroll: true });
          }
        }

        if (push) history.pushState({}, '', url);
        this.applyStoredColumns();
        window.itrh?.reveal?.();

        if (scrollTop) {
          const top = this.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('itrh-collection')) customElements.define('itrh-collection', ItrhCollection);
})();